import type {Client} from "typesense";
import type {CollectionCreateSchema} from "typesense/lib/Typesense/Collections";
import type {CollectionDropFieldSchema, CollectionFieldSchema} from "typesense/lib/Typesense/Collection";

/**
 * Serialise a field definition to a stable string key for comparison.
 * We compare by name only for the diff — property changes (type, optional,
 * index, etc.) are flagged as manual-intervention items.
 */
function fieldKey(f: {name: string}): string {
    return f.name;
}

/**
 * Relevant field properties that, when they differ between the desired and
 * live schemas, indicate a breaking change requiring manual intervention.
 */
const FIELD_PROPERTIES_TO_COMPARE: readonly string[] = [
    "type",
    "optional",
    "facet",
    "index",
    "sort",
    "infix",
    "stem",
    "num_dim",
];

/**
 * Property changes that are safe to auto-apply via a drop + re-add PATCH. These
 * only affect how a field is indexed; Typesense rebuilds the index from the
 * stored document JSON, so no data is reinterpreted or lost. Everything else
 * (notably `type`, which can fail coercion of stored values, and `num_dim`,
 * which rebuilds vectors that aren't recoverable from stored scalars) is
 * routed to manual intervention.
 *
 * `optional` is direction-sensitive: loosening (`false` → `true`) is always
 * safe, but tightening (`true` → `false`) would silently exclude documents
 * that never stored the field from its index, so only loosening is
 * auto-reconcilable — see {@link isDiffReconcilable}.
 */
const AUTO_RECONCILABLE_PROPERTIES: ReadonlySet<string> = new Set([
    "index",
    "facet",
    "sort",
    "infix",
    "stem",
    "optional",
]);

/**
 * Collect every relevant-property difference between a desired and a live
 * field definition. Properties the desired schema does not specify are not
 * compared.
 */
function fieldPropertyDifferences(
    desiredField: {name: string; [key: string]: unknown},
    liveField: CollectionFieldSchema,
): FieldDifference[] {
    const diffs: FieldDifference[] = [];
    for (const prop of FIELD_PROPERTIES_TO_COMPARE) {
        const desiredValue = desiredField[prop];
        const liveValue = liveField[prop];

        // Skip comparison if the desired schema doesn't specify the property
        if (desiredValue === undefined) continue;

        if (desiredValue !== liveValue) {
            diffs.push({fieldName: desiredField.name, property: prop, desired: desiredValue, live: liveValue});
        }
    }
    return diffs;
}

function isDiffReconcilable(diff: FieldDifference): boolean {
    if (!AUTO_RECONCILABLE_PROPERTIES.has(diff.property)) return false;
    if (diff.property === "optional" && diff.desired === false) return false;
    return true;
}

export interface FieldDifference {
    fieldName: string;
    property: string;
    desired: unknown;
    live: unknown;
}

export interface EnsureSchemaResult {
    /** Whether the collection already existed before this call. */
    existed: boolean;
    /** Whether the collection was created from scratch during this call. */
    created: boolean;
    /**
     * Field names added (or, in dry-run, that *would* be added) to an existing
     * collection. For a missing collection this lists every field of the schema.
     */
    fieldsAdded: string[];
    /**
     * Field names reconciled (or, in dry-run, that *would* be) via a drop +
     * re-add PATCH. Only populated when `autoReconcile` is enabled.
     */
    fieldsReconciled: string[];
    /** Field-level property differences that require manual intervention. */
    manualChangesNeeded: FieldDifference[];
}

export interface EnsureSchemaOptions {
    /**
     * When false, computes the diff but performs no mutation (no `create`,
     * no `update`). The failure signal (`manualChangesNeeded`) is still real.
     * Defaults to true.
     */
    apply?: boolean;
    /**
     * When true, field property changes limited to safe index-only flags
     * (`index`/`facet`/`sort`/`infix`/`stem`/`optional`) are auto-applied via an
     * atomic drop + re-add PATCH, which reindexes from stored data with no
     * re-import. `type`/`num_dim` changes, and any field whose live definition is
     * `store:false` (where the raw value isn't retained), are still routed to
     * `manualChangesNeeded`. Defaults to false.
     */
    autoReconcile?: boolean;
    logger?: {info: (msg: string) => void; warn: (msg: string) => void};
    /**
     * Interval between live-schema polls while waiting for a collection
     * update that is still running on the server. Defaults to 10_000.
     */
    updatePollIntervalMs?: number;
    /**
     * Maximum time to wait for a still-running collection update to finish
     * before giving up. Defaults to 20 minutes.
     */
    updateWaitDeadlineMs?: number;
    /** Injectable sleep for tests. Defaults to setTimeout. */
    sleep?: (ms: number) => Promise<void>;
    /** Injectable clock for tests. Defaults to Date.now. */
    now?: () => number;
}

const DEFAULT_UPDATE_POLL_INTERVAL_MS = 10_000;
const DEFAULT_UPDATE_WAIT_DEADLINE_MS = 20 * 60_000;

/**
 * Decide whether a `collection.update` error can mean the update is still
 * running on the server: either the server reported another update in
 * progress (HTTP 422) or the request timed out while the server kept working.
 * Returns a short reason string, or null for unrelated errors.
 */
function updateStillRunningReason(err: unknown): string | null {
    const message = err instanceof Error ? err.message : String(err);
    const httpStatus = (err as {httpStatus?: unknown}).httpStatus;
    if (
        typeof httpStatus === "number" &&
        httpStatus === 422 &&
        message.includes("collection update operation is in progress")
    ) {
        return "another collection update operation is in progress";
    }
    const code = (err as {code?: unknown}).code;
    if (message.includes("timeout") || message.includes("ECONNABORTED") || code === "ECONNABORTED") {
        return `the update request timed out (${message})`;
    }
    return null;
}

/**
 * Ensures a Typesense collection matches the code-defined schema.
 *
 * 1. If the collection does not exist, creates it.
 * 2. If the collection exists, compares live fields against the desired
 *    schema. Reconcilable fields are reindexed one at a time before missing
 *    fields are added. The schema is read again after each update. When an
 *    update call fails because the update is still running on the server, the
 *    live schema is polled and the PATCH is retried until the live schema
 *    matches the desired fields or the wait deadline passes.
 * 3. Fields that exist in both but differ in type/properties are logged as
 *    needing manual intervention (not auto-modified).
 * 4. Fields present in the live collection but absent from the desired schema
 *    are left untouched (no auto-drop).
 *
 * @param client   Typesense admin client
 * @param schema   The desired CollectionCreateSchema (from schemas.ts)
 * @param options  `apply` (default true) toggles mutation vs dry-run; `logger` defaults to console
 */
export async function ensureTypesenseSchema(
    client: Client,
    schema: CollectionCreateSchema,
    options: EnsureSchemaOptions = {},
): Promise<EnsureSchemaResult> {
    const apply = options.apply ?? true;
    const autoReconcile = options.autoReconcile ?? false;
    const logger = options.logger ?? console;
    const updatePollIntervalMs = options.updatePollIntervalMs ?? DEFAULT_UPDATE_POLL_INTERVAL_MS;
    const updateWaitDeadlineMs = options.updateWaitDeadlineMs ?? DEFAULT_UPDATE_WAIT_DEADLINE_MS;
    const sleep = options.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
    const now = options.now ?? Date.now;
    const collectionName = schema.name;
    const collection = client.collections(collectionName);

    // 1. Check if collection exists
    const exists = await collection.exists();

    if (!exists) {
        if (!apply) {
            // Dry-run: report what would be created, mutate nothing.
            logger.info(
                `[ensureTypesenseSchema] (dry-run) would create collection "${collectionName}" with ${schema.fields.length} fields`,
            );
            return {
                existed: false,
                created: false,
                fieldsAdded: schema.fields.map((f) => f.name),
                fieldsReconciled: [],
                manualChangesNeeded: [],
            };
        }
        await client.collections().create(schema);
        logger.info(
            `[ensureTypesenseSchema] Created collection "${collectionName}" with ${schema.fields.length} fields`,
        );
        return {
            existed: false,
            created: true,
            fieldsAdded: schema.fields.map((f) => f.name),
            fieldsReconciled: [],
            manualChangesNeeded: [],
        };
    }

    function planChanges(liveFields: CollectionFieldSchema[]): {
        fieldsToAdd: CollectionFieldSchema[];
        fieldsToReconcile: CollectionFieldSchema[];
        manualChangesNeeded: FieldDifference[];
    } {
        const liveFieldsByName: {[name: string]: CollectionFieldSchema} = {};
        for (const f of liveFields) {
            liveFieldsByName[fieldKey(f)] = f;
        }

        const fieldsToAdd: CollectionFieldSchema[] = [];
        const fieldsToReconcile: CollectionFieldSchema[] = [];
        const manualChangesNeeded: FieldDifference[] = [];

        for (const desiredField of schema.fields) {
            const name = fieldKey(desiredField);
            const liveField = liveFieldsByName[name];

            if (!liveField) {
                fieldsToAdd.push(desiredField as CollectionFieldSchema);
                continue;
            }

            const diffs = fieldPropertyDifferences(desiredField, liveField);

            if (diffs.length === 0) continue;

            const allDiffsReconcilable = diffs.every(isDiffReconcilable);

            if (!autoReconcile || !allDiffsReconcilable) {
                manualChangesNeeded.push(...diffs);
                continue;
            }

            if (liveField.store === false) {
                logger.warn(
                    `[ensureTypesenseSchema] Field "${name}" in "${collectionName}" has reconcilable ` +
                        `property drift but is store:false on the live cluster; a drop + re-add would ` +
                        `discard its data. Routing to manual intervention (re-import required).`,
                );
                manualChangesNeeded.push(...diffs);
                continue;
            }

            fieldsToReconcile.push(desiredField as CollectionFieldSchema);
        }

        return {fieldsToAdd, fieldsToReconcile, manualChangesNeeded};
    }

    async function applyUpdate(
        fieldsToAdd: CollectionFieldSchema[],
        fieldsToReconcile: CollectionFieldSchema[],
    ): Promise<void> {
        const updateFields: (CollectionFieldSchema | CollectionDropFieldSchema)[] = [...fieldsToAdd];
        for (const field of fieldsToReconcile) {
            updateFields.push({name: field.name, drop: true});
            updateFields.push(field);
        }

        const deadline = now() + updateWaitDeadlineMs;
        let lastUpdateError: unknown;
        for (;;) {
            try {
                await collection.update({fields: updateFields});
                return;
            } catch (err) {
                const reason = updateStillRunningReason(err);
                if (reason === null) throw err;
                lastUpdateError = err;
                logger.info(
                    `[ensureTypesenseSchema] Update of "${collectionName}" could not complete (${reason}); waiting for the live schema to match before retrying`,
                );
            }

            if (now() > deadline) {
                const waitedS = Math.round(updateWaitDeadlineMs / 1000);
                const message = lastUpdateError instanceof Error ? lastUpdateError.message : String(lastUpdateError);
                throw new Error(
                    `Timed out after ${waitedS}s waiting for the schema update of "${collectionName}" to finish. Last error: ${message}`,
                );
            }
            await sleep(updatePollIntervalMs);
            try {
                const refreshedSchema = await collection.retrieve();
                if (liveSchemaMatches(refreshedSchema.fields, fieldsToAdd, fieldsToReconcile)) return;
            } catch {
                // Transient retrieve failure. Retry the PATCH.
            }
        }
    }

    // 2. Retrieve live schema and diff fields
    let liveSchema = await collection.retrieve();
    const initialPlan = planChanges(liveSchema.fields);
    const initialFieldsToAdd = initialPlan.fieldsToAdd;
    let plan = initialPlan;
    const addedFieldNames = new Set<string>();
    const reconciledFieldNames = new Set<string>();

    if (!apply) {
        const addedNames = plan.fieldsToAdd.map((f) => f.name).join(", ");
        const reconciledNames = plan.fieldsToReconcile.map((f) => f.name).join(", ");
        if (plan.fieldsToAdd.length > 0) {
            logger.info(
                `[ensureTypesenseSchema] (dry-run) would add ${plan.fieldsToAdd.length} field(s) to "${collectionName}": ${addedNames}`,
            );
        }
        if (plan.fieldsToReconcile.length > 0) {
            logger.info(
                `[ensureTypesenseSchema] (dry-run) would reconcile ${plan.fieldsToReconcile.length} field(s) in "${collectionName}" via drop+re-add: ${reconciledNames}`,
            );
        }
    } else {
        for (;;) {
            const fieldToReconcile = [...plan.fieldsToReconcile].sort(
                (a, b) => a.name.split(".").length - b.name.split(".").length,
            )[0];

            if (fieldToReconcile) {
                await applyUpdate([], [fieldToReconcile]);
                reconciledFieldNames.add(fieldToReconcile.name);
                logger.info(
                    `[ensureTypesenseSchema] Reconciled field "${fieldToReconcile.name}" in "${collectionName}" via drop+re-add`,
                );
                liveSchema = await collection.retrieve();
                plan = planChanges(liveSchema.fields);
                continue;
            }

            if (plan.fieldsToAdd.length > 0) {
                const fieldsToAdd = plan.fieldsToAdd;
                await applyUpdate(fieldsToAdd, []);
                for (const field of fieldsToAdd) {
                    addedFieldNames.add(field.name);
                }
                logger.info(
                    `[ensureTypesenseSchema] Added ${fieldsToAdd.length} field(s) to "${collectionName}": ${fieldsToAdd
                        .map((f) => f.name)
                        .join(", ")}`,
                );
                liveSchema = await collection.retrieve();
                plan = planChanges(liveSchema.fields);
                continue;
            }

            break;
        }
    }

    const manualChangesNeeded = plan.manualChangesNeeded;
    // 3. Warn about manual changes
    for (const diff of manualChangesNeeded) {
        logger.warn(
            `[ensureTypesenseSchema] Field "${diff.fieldName}" in "${collectionName}" has a ` +
                `${diff.property} mismatch: desired=${JSON.stringify(diff.desired)}, live=${JSON.stringify(
                    diff.live,
                )}. ` +
                `Manual intervention required (drop + re-add).`,
        );
    }

    // 4. Check for extra fields in live that aren't in desired (informational only)
    const desiredFieldNames = new Set(schema.fields.map((f) => fieldKey(f)));
    const extraLiveFields = liveSchema.fields.filter((f) => !desiredFieldNames.has(fieldKey(f)));
    if (extraLiveFields.length > 0) {
        logger.info(
            `[ensureTypesenseSchema] "${collectionName}" has ${
                extraLiveFields.length
            } extra field(s) not in desired schema: ${extraLiveFields.map((f) => f.name).join(", ")}`,
        );
    }

    return {
        existed: true,
        created: false,
        fieldsAdded: apply ? [...addedFieldNames] : initialFieldsToAdd.map((f) => f.name),
        fieldsReconciled: apply ? [...reconciledFieldNames] : initialPlan.fieldsToReconcile.map((f) => f.name),
        manualChangesNeeded,
    };
}

/**
 * Check that a live schema contains every added field, and every reconciled
 * field with the desired values for the compared properties.
 */
function liveSchemaMatches(
    liveFields: CollectionFieldSchema[],
    fieldsToAdd: CollectionFieldSchema[],
    fieldsToReconcile: CollectionFieldSchema[],
): boolean {
    const liveFieldsByName: {[name: string]: CollectionFieldSchema} = {};
    for (const f of liveFields) {
        liveFieldsByName[fieldKey(f)] = f;
    }
    for (const field of fieldsToAdd) {
        if (!liveFieldsByName[fieldKey(field)]) return false;
    }
    for (const field of fieldsToReconcile) {
        const liveField = liveFieldsByName[fieldKey(field)];
        if (!liveField) return false;
        if (fieldPropertyDifferences(field, liveField).length > 0) return false;
    }
    return true;
}
